import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient'; // Assuming supabaseClient is in the parent directory
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Chip,
  useTheme,
  useMediaQuery,
  Container,
  IconButton,
  Tooltip,
  Stack,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { DOCUMENT_STATUSES, DOCUMENT_STATUS_LABELS } from '../config/constants'; // Import statussen
import { useTheme as useCustomTheme } from '../ThemeContext';
import clsx from 'clsx';

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    console.error("Error formatting date:", e);
    return 'Ongeldige datum';
  }
};

const ALL_STATUSES_KEY = 'all'; // Key for the 'all statuses' filter option
const ROWS_PER_PAGE_OPTIONS = [50, 100, 250, 500]; // Define options here

function Library() {
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES_KEY); // State for status filter
  const [page, setPage] = useState(0); // State for current page (0-indexed)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]); // State for rows per page, default 50
  const [sortBy, setSortBy] = useState('created_at'); // default sort
  const [sortDirection, setSortDirection] = useState('desc');
  const theme = useTheme();
  const { theme: customTheme } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('documents')
          .select('id, name, created_at, status, projects(id, name), profiles!reviewed_by(full_name)')
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error("Supabase fetch error:", fetchError);
          throw fetchError;
        }
        setAllDocuments(data || []);
      } catch (err) {
        console.error("Error fetching documents:", err);
        setError('Kon documenten niet laden. Controleer de console voor details.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Reset page to 0 when search term changes
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset page
  };

  // Reset page to 0 when status filter changes
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset page
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const processedDocuments = useMemo(() => {
    let filtered = allDocuments;
    if (statusFilter !== ALL_STATUSES_KEY) {
      filtered = allDocuments.filter(doc => doc.status === statusFilter);
    }

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(lowerSearchTerm) ||
        (doc.projects?.name && doc.projects.name.toLowerCase().includes(lowerSearchTerm))
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'project':
          aValue = a.projects?.name || '';
          bValue = b.projects?.name || '';
          break;
        case 'created_at':
        default:
          aValue = a.created_at;
          bValue = b.created_at;
          break;
      }
      if (sortBy === 'created_at') {
        return sortDirection === 'asc'
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      } else {
        return sortDirection === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }
    });

    return sorted;
  }, [allDocuments, searchTerm, sortBy, sortDirection, statusFilter]);

  // Calculate documents for the current page
  const paginatedDocuments = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return processedDocuments.slice(startIndex, startIndex + rowsPerPage);
  }, [processedDocuments, page, rowsPerPage]);

  const getStatusColor = (status) => {
    switch (status) {
      case DOCUMENT_STATUSES.INTAKE:
        return 'info';
      case DOCUMENT_STATUSES.TO_REVIEW:
        return 'warning';
      case DOCUMENT_STATUSES.REVIEWED:
        return 'success';
      case DOCUMENT_STATUSES.EXPORTED:
        return 'primary';
      default:
        return 'default';
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (active, direction) => {
    if (!active) return <ArrowDropDownIcon sx={{ opacity: 0.3, ml: 0.5, fontSize: 20 }} />;
    return direction === 'asc'
      ? <ArrowDropUpIcon sx={{ color: 'primary.main', ml: 0.5, fontSize: 20 }} />
      : <ArrowDropDownIcon sx={{ color: 'primary.main', ml: 0.5, fontSize: 20 }} />;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.contrastText',
          py: 4,
          px: 3,
          mb: 3
        }}
      >
        <Container maxWidth="xl">
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Documenten Bibliotheek
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Beheer en bekijk alle documenten in de bibliotheek
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {/* Search and Filters Section */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            mb: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems={{ xs: 'stretch', sm: 'center' }}
            divider={<Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />}
          >
            <TextField
              size="small"
              placeholder="Zoeken op naam of project..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper'
                }
              }}
            />
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              sx={{ minWidth: { sm: 'auto', xs: '100%' } }}
            >
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilterChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" color="action" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value={ALL_STATUSES_KEY}>Alle Statussen</MenuItem>
                  {Object.entries(DOCUMENT_STATUS_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="rows-per-page-label">Aantal</InputLabel>
                <Select
                  labelId="rows-per-page-label"
                  value={rowsPerPage}
                  label="Aantal"
                  onChange={handleChangeRowsPerPage}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option} per pagina
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Paper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        {!loading && !error && (
          <>
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{ 
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Table stickyHeader aria-label="document library table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 600, cursor: 'pointer', bgcolor: 'background.paper', borderBottom: 2, borderColor: 'divider', userSelect: 'none', transition: 'background 0.2s', '&:hover': { bgcolor: 'action.hover' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}
                      onClick={() => handleSort('name')}
                      aria-sort={sortBy === 'name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title="Sorteer op naam"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Document Naam
                        {getSortIcon(sortBy === 'name', sortDirection)}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, cursor: 'pointer', bgcolor: 'background.paper', borderBottom: 2, borderColor: 'divider', userSelect: 'none', transition: 'background 0.2s', '&:hover': { bgcolor: 'action.hover' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}
                      onClick={() => handleSort('project')}
                      aria-sort={sortBy === 'project' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title="Sorteer op project"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Project
                        {getSortIcon(sortBy === 'project', sortDirection)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', borderBottom: 2, borderColor: 'divider' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', borderBottom: 2, borderColor: 'divider' }}>Beoordeeld door</TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, cursor: 'pointer', bgcolor: 'background.paper', borderBottom: 2, borderColor: 'divider', userSelect: 'none', transition: 'background 0.2s', '&:hover': { bgcolor: 'action.hover' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}
                      onClick={() => handleSort('created_at')}
                      aria-sort={sortBy === 'created_at' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title="Sorteer op aanmaakdatum"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Aanmaakdatum
                        {getSortIcon(sortBy === 'created_at', sortDirection)}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDocuments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {processedDocuments.length === 0
                            ? 'Geen documenten gevonden.'
                            : 'Geen documenten gevonden die voldoen aan de filters op deze pagina.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedDocuments.map((doc) => (
                      <TableRow
                        key={doc.id}
                        hover
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}
                          title={doc.name}
                        >
                          {doc.name}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}
                          title={doc.projects?.name || '-'}
                        >
                          {doc.projects?.name || '-'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={DOCUMENT_STATUS_LABELS[doc.status] || doc.status}
                            size="small"
                            color={getStatusColor(doc.status)}
                            sx={{ 
                              fontWeight: 500,
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {doc.profiles?.full_name ? (
                            <Chip
                              label={doc.profiles.full_name}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              nog niet beoordeeld
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}
                          title={formatDate(doc.created_at)}
                        >
                          {formatDate(doc.created_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={processedDocuments.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Rijen per pagina:"
              sx={{ 
                mt: 2,
                '.MuiTablePagination-select': {
                  bgcolor: 'background.paper'
                }
              }}
            />
          </>
        )}
      </Container>
    </Box>
  );
}

export default Library;
